import React, { useEffect } from "react";
import { Box, Divider, IconButton, Paper, Stack, Tooltip } from "@mui/material";
import {
  FormatAlignCenter,
  FormatAlignLeft,
  FormatAlignRight,
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatUnderlined,
  Link as LinkIcon,
} from "@mui/icons-material";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  disabled?: boolean;
}

const normalizeHtml = (html?: string) => {
  if (!html) return "";
  return html.replace(/\s+/g, " ").trim();
};

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = "Write your content...",
  minHeight = 260,
  disabled = false,
}) => {
  const editor = useEditor({
    editable: !disabled,
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: "https",
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "ff-rich-editor-content",
      },
    },
    onUpdate: ({ editor: currentEditor }) => {
      onChange(currentEditor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  const incomingHtml = normalizeHtml(value || "");

  useEffect(() => {
    if (!editor) return;
    const currentHtml = normalizeHtml(editor.getHTML() || "");
    if (incomingHtml === currentHtml) return;
    editor.commands.setContent(value || "", { emitUpdate: false });
  }, [editor, incomingHtml, value]);

  const setLink = () => {
    if (!editor || disabled) return;

    const previousUrl = editor.getAttributes("link").href as string;
    const entered = window.prompt("Enter link URL", previousUrl || "https://");
    if (entered === null) return;

    const raw = entered.trim();
    if (!raw) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    const finalUrl = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    editor.chain().focus().extendMarkRange("link").setLink({ href: finalUrl }).run();
  };

  const activeBg = "rgba(216, 154, 122, 0.16)";
  const activeColor = "#B56D46";

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "12px",
        border: "1px solid rgba(0,0,0,0.08)",
        bgcolor: "#FEFDFB",
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        spacing={0.4}
        sx={{
          p: 1,
          flexWrap: "wrap",
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          bgcolor: "#FFF",
        }}
      >
        <Tooltip title="Bold">
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => editor?.chain().focus().toggleBold().run()}
              sx={{
                bgcolor: editor?.isActive("bold") ? activeBg : "transparent",
                color: editor?.isActive("bold") ? activeColor : "#6B7280",
              }}
            >
              <FormatBold fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Italic">
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => editor?.chain().focus().toggleItalic().run()}
              sx={{
                bgcolor: editor?.isActive("italic") ? activeBg : "transparent",
                color: editor?.isActive("italic") ? activeColor : "#6B7280",
              }}
            >
              <FormatItalic fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Underline">
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => editor?.chain().focus().toggleUnderline().run()}
              sx={{
                bgcolor: editor?.isActive("underline") ? activeBg : "transparent",
                color: editor?.isActive("underline") ? activeColor : "#6B7280",
              }}
            >
              <FormatUnderlined fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.4 }} />

        <Tooltip title="Bullet List">
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
              sx={{
                bgcolor: editor?.isActive("bulletList") ? activeBg : "transparent",
                color: editor?.isActive("bulletList") ? activeColor : "#6B7280",
              }}
            >
              <FormatListBulleted fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Insert Link">
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={setLink}
              sx={{
                bgcolor: editor?.isActive("link") ? activeBg : "transparent",
                color: editor?.isActive("link") ? activeColor : "#6B7280",
              }}
            >
              <LinkIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.4 }} />

        <Tooltip title="Align Left">
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => editor?.chain().focus().setTextAlign("left").run()}
              sx={{
                bgcolor: editor?.isActive({ textAlign: "left" }) ? activeBg : "transparent",
                color: editor?.isActive({ textAlign: "left" }) ? activeColor : "#6B7280",
              }}
            >
              <FormatAlignLeft fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Align Center">
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => editor?.chain().focus().setTextAlign("center").run()}
              sx={{
                bgcolor: editor?.isActive({ textAlign: "center" }) ? activeBg : "transparent",
                color: editor?.isActive({ textAlign: "center" }) ? activeColor : "#6B7280",
              }}
            >
              <FormatAlignCenter fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Align Right">
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => editor?.chain().focus().setTextAlign("right").run()}
              sx={{
                bgcolor: editor?.isActive({ textAlign: "right" }) ? activeBg : "transparent",
                color: editor?.isActive({ textAlign: "right" }) ? activeColor : "#6B7280",
              }}
            >
              <FormatAlignRight fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Box
        sx={{
          p: 1.5,
          minHeight,
          "& .ff-rich-editor-content": {
            minHeight: minHeight - 24,
            outline: "none",
            color: "#3D3630",
            fontSize: "0.95rem",
            lineHeight: 1.9,
            fontFamily: "'Georgia', 'Times New Roman', serif",
          },
          "& .ff-rich-editor-content p": {
            margin: "0 0 0.75rem",
          },
          "& .ff-rich-editor-content p:last-of-type": {
            marginBottom: 0,
          },
          "& .ff-rich-editor-content ul": {
            margin: "0 0 0.75rem",
            paddingLeft: "1.25rem",
          },
          "& .ff-rich-editor-content a": {
            color: "#2563EB",
            textDecoration: "underline",
          },
          "& .ff-rich-editor-content.is-editor-empty:first-of-type::before": {
            content: 'attr(data-placeholder)',
            color: "#A89E94",
            float: "left",
            height: 0,
            pointerEvents: "none",
            fontStyle: "italic",
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Paper>
  );
};

export default RichTextEditor;
